import React from 'react';
import TitleCard from './components/TitleCard';
import ScheduleCard from './components/ScheduleCard';
import '../../Competition.css';
import DetailList from './components/DetailList';
import { formatDate, ICompetition, ILocation } from '../../Competition';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const timezones = require('../../../../../libs/timezones/timezones.json');

const styles = {
  imageContainer: {
    width: '100%',
    height: 264,
    borderRadius: 10,
    marginTop: 66,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: '0.2s',
  },
  cardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    gap: '0 16px',
  },
};

const getDuration = (competition: ICompetition): string => {
  const format = 'YYYY.MM.DD';
  const { durationFrom, durationTo, timezone } = competition;
  return `${formatDate(durationFrom, timezone, format)} - ${formatDate(durationTo, timezone, format)}`;
};

const getTimezone = (timezone: string): string => {
  const tz = Object.entries(timezones).find((entry) => entry[1] === timezone);
  return tz ? tz[0].substring(1, 10) : 'N/A';
};

const getDetailedAddress = (location: ILocation): string => {
  const res = [];
  if (!location) {
    return 'N/A';
  }
  if (location.streetName) {
    if (location.streetNumber) {
      res.push(`${location.streetNumber} ${location.streetName}`);
    }
    res.push(location.streetName);
  }
  if (location.city) {
    res.push(location.city);
  }
  if (location.state) {
    res.push(location.state);
  }
  if (location.country) {
    res.push(location.country);
  }
  if (!res.length) {
    return 'N/A';
  }
  return res.length === 1 ? res[0] : res.join(', ');
};

const Header = ({
  competition, onApply, userRoles,
  onWithdraw, withdrawButtonVisible, onMessage, applyButtonVisible, evaluationStatus, onGenerateSummary, onAddPin, pin,
}: Props) => (
  <>
    <div style={styles.imageContainer}>
      <div
        style={{
          ...styles.image,
          backgroundImage: `url(${competition.image || '/images/profile-background.png'})`,
        }}
      />
    </div>
    <div style={styles.cardContainer}>
      <div>
        <TitleCard
          name={competition.name}
          tag={competition.tag}
          applicationTypes={competition.applicationTypes}
          userRoles={userRoles}
          onMessage={onMessage}
          onWithdraw={onWithdraw}
          withdrawButtonVisible={withdrawButtonVisible}
          evaluationStatus={evaluationStatus}
          onGenerateSummary={onGenerateSummary}
          onAddPin={onAddPin}
          pin={pin}
        />
        <DetailList
          location={competition.location
            ? getDetailedAddress(competition.location) : competition.city}
          duration={getDuration(competition)}
          deadline={formatDate(competition.deadline, competition.timezone, 'YYYY.MM.DD HH:mm')}
          timezone={getTimezone(competition.timezone)}
          isOnline={competition.isOnline}
        />
      </div>
      <div>
        <ScheduleCard
          applyButtonVisible={applyButtonVisible}
          className='schedule-desktop'
          schedules={competition.schedules}
          leadButtonText={competition.leadButtonText}
          style={{ marginTop: -200, marginRight: -20, width: 288 }}
          timezone={competition.timezone}
          onApply={onApply}
          withdrawButtonVisible={withdrawButtonVisible}
        />
      </div>
    </div>
    <ScheduleCard
      applyButtonVisible={applyButtonVisible}
      className='schedule-mobile'
      style={{ marginTop: 0 }}
      schedules={competition.schedules}
      leadButtonText={competition.leadButtonText}
      timezone={competition.timezone}
      onApply={onApply}
      withdrawButtonVisible={withdrawButtonVisible}
    />
  </>
);

interface Props {
  competition: ICompetition;
  userRoles: string[];
  onApply: () => void;
  onWithdraw: () => void;
  withdrawButtonVisible: boolean;
  applyButtonVisible: boolean;
  onMessage: () => void;
  onGenerateSummary: () => void;
  onAddPin:()=>void;
  pin:boolean;
  evaluationStatus: 'INIT' | 'OPEN' | 'CLOSED';
}

export default Header;
